import { KnowledgeSearchPageHit } from '@digital-twins/shared-types'

import { parseVideoTimestampLabel } from './videoUrl'

const TS_SPAN_HEADING_RE = /^#{2,3}\s*\[(\d{1,2}:\d{2}(?::\d{2})?)\]/

export function pageHitKey(page: KnowledgeSearchPageHit): string {
  return `${page.unitKind}-${page.unitIndex}-${page.label || ''}`
}

function timestampLabelFromPage(page: KnowledgeSearchPageHit): string | null {
  const fromLabel = (page.label || '').trim()
  if (fromLabel) {
    return fromLabel
  }
  const match = (page.snippet || '').match(TS_SPAN_HEADING_RE)
  return match?.[1] ?? null
}

export function pageTimestampSeconds(page: KnowledgeSearchPageHit): number {
  const label = timestampLabelFromPage(page)
  if (!label) {
    return page.unitIndex
  }
  return parseVideoTimestampLabel(label) ?? page.unitIndex
}

export function sortTimelinePages(
  pages: Array<KnowledgeSearchPageHit>,
  timestampMode: boolean,
): Array<KnowledgeSearchPageHit> {
  return [...pages].sort((a, b) => {
    if (timestampMode) {
      return pageTimestampSeconds(a) - pageTimestampSeconds(b)
    }
    return a.unitIndex - b.unitIndex
  })
}

/**
 * Map a page or timestamp hit to a 2–98% position along the document timeline.
 * Inset keeps edge markers and labels from clipping.
 */
export function timelineMarkerPercent(
  page: KnowledgeSearchPageHit,
  totalExtent: number,
  timestampMode: boolean,
): number {
  if (totalExtent <= 1) {
    return 50
  }
  let raw = 0
  if (timestampMode) {
    const seconds = pageTimestampSeconds(page)
    raw = Math.min(100, Math.max(0, (seconds / totalExtent) * 100))
  } else {
    raw = Math.min(
      100,
      Math.max(0, ((page.unitIndex - 1) / (totalExtent - 1)) * 100),
    )
  }
  return 2 + (raw / 100) * 96
}

export function inferTimelineExtent(
  pages: Array<KnowledgeSearchPageHit>,
  timestampMode: boolean,
  documentPageCount: number | null,
): number {
  if (timestampMode) {
    const hitMax = pages.reduce(
      (max, page) => Math.max(max, pageTimestampSeconds(page)),
      0,
    )
    return Math.max(hitMax, 1)
  }
  if (documentPageCount != null && documentPageCount > 0) {
    return documentPageCount
  }
  return pages.reduce((max, page) => Math.max(max, page.unitIndex), 1)
}
